import { Component, inject, Input, OnDestroy } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { getExtensionRules, PRODUCT_RELATED_RULE_ENTITIES } from '../../../../../shared/all-extension-rules';
import { DynamicRule } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { CheckoutExtensionUI, CheckoutUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { capitalize } from '@utils/string-utils';
import {
  createGroupDiscountFormGroup,
  createRuleGroupFormGroup,
  DEFAULT_RULE_GROUP,
  GroupDiscountApplyToMode,
  HideProductsAlreadyInCart,
  NoItemsToDisplay,
  PrioritizeRecommendedItemsBy,
  ProductsInCartMode,
  UpsellProduct,
  UpsellRecommendedProductsMode,
} from '../../models';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { mergeEditedProducts } from '@app/sharedModule/product-selection/utils/merge-edited-products';
import {
  EditProductModalComponent,
  EditProductModalResult,
} from '@app/sharedModule/product-selection/components/edit-product-modal/edit-product-modal.component';
import { ProductListItem } from '@storybook/components/product-list/product-list.component';
import { firstValueFrom, Subject } from 'rxjs';
import { mapExtensionRulesToGqlQuery } from '@pdqdev/pdq-shared/dist/utils/extensions-rules-to-graphql';
import { ShopService } from '@app/services/shops/shop.service';
interface PriorityOption {
  value: PrioritizeRecommendedItemsBy;
  label: string;
  description: string;
}

interface DisplayTypeOption {
  text: string;
  value: CheckoutUpsellExtension.DisplayType;
  tooltip?: string;
  tooltipIcon?: string;
  defaultChecked?: boolean;
  icon?: string;
}

interface VariantSelectorOption {
  value: string;
  label: string;
  helpText: string;
}

type UpsellProductWithDetails = UpsellProduct & { imageUrl?: string; sku?: string; price?: string };

@Component({
  selector: 'checkout-upsell-product-selection',
  templateUrl: './checkout-upsell-product-selection.component.html',
  styleUrls: ['./checkout-upsell-product-selection.component.scss'],
  standalone: false,
})
export class CheckoutUpsellProductSelectionComponent implements OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shopCurrencySymbol: string;
  @Input() loadedValues: any;
  @Input() defaultCurrencySymbol: string;
  @Input() isAutonomousDiscount = false;

  private shopService = inject(ShopService);

  readonly dragHandleRectangles = Array(6).fill(0);
  DiscountType = CheckoutUpsellExtension.DiscountType;

  readonly ruleGroupsFieldName = 'productSelection';
  private destroy$ = new Subject<void>();
  loadingRuleGroupIndex: number | null = null;

  // Rules config for Products in Cart (smart selection)
  productsInCartRules = getExtensionRules();
  productsInCartEntities = this.productsInCartRules.map((r) => r.entity);

  // Rules config for Upsell Products (smart selection)
  upsellProductsRules = getExtensionRules({ onlyRules: PRODUCT_RELATED_RULE_ENTITIES });
  upsellProductsEntities = this.upsellProductsRules.map((r) => r.entity);

  prioritizeOptions: PriorityOption[] = [
    {
      value: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.PROFITABILITY,
      label: 'Profitability',
      description: 'Show items with the highest profit margins first.',
    },
    {
      value: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.HIGH_PRICE,
      label: 'High Price',
      description: 'Show most expensive items first.',
    },
    {
      value: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.LOW_PRICE,
      label: 'Low Price',
      description: 'Show the cheapest items that help unlock free shipping first.',
    },
    {
      value: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.SELLING_PLAN,
      label: 'Selling Plan',
      description: 'Show items only from a specific selling plan.',
    },
    {
      value: CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.MANUAL,
      label: 'Manual',
      description: 'Use the order you set when adding products.',
    },
  ];

  hideProductsOptions: { value: HideProductsAlreadyInCart; label: string; description: string }[] = [
    {
      value: 'parentProduct',
      label: 'Parent product only',
      description: 'Hide if any variant of the product is in cart',
    },
    {
      value: 'specificVariant',
      label: 'Specific variant only',
      description: 'Only hide the exact variant in cart (allow other variants)',
    },
  ];

  ifNoItemsOptions: { value: NoItemsToDisplay; label: string }[] = [
    { value: 'hide', label: 'Hide section' },
    { value: 'showShopifyRecommendations', label: 'Show Shopify recommendations' },
  ];

  displayTypeOptions: DisplayTypeOption[] = [
    {
      text: "Don't display",
      value: CheckoutUpsellExtension.DisplayType.HIDE_PRODUCTS,
      icon: 'preview_eye_crossed',
    },
    {
      text: 'Carousel',
      value: CheckoutUpsellExtension.DisplayType.CARDS,
      tooltip: 'New upsells use this format on mobile. Existing upsells will continue using the rows format on mobile.',
      tooltipIcon: 'attention',
      defaultChecked: true,
      icon: 'carousel',
    },
    {
      text: 'List',
      value: CheckoutUpsellExtension.DisplayType.ROW,
      icon: 'two-rectangle',
    },
    {
      text: 'Compact',
      value: CheckoutUpsellExtension.DisplayType.COMPACT,
      icon: 'rectangle',
    },
  ];

  CarouselNavigationStyle = CheckoutUpsellExtension.CarouselNavigationStyle;

  carouselNavigationStyleOptions = [
    {
      value: CheckoutUpsellExtension.CarouselNavigationStyle.ARROWS,
      text: 'Arrows',
      description: 'Navigate between products using arrow buttons',
    },
    {
      value: CheckoutUpsellExtension.CarouselNavigationStyle.SCROLL,
      text: 'Scroll Bar',
      description: 'Scroll horizontally through the product list with a draggable bar',
    },
  ];

  variantSelectorOptions: VariantSelectorOption[] = [
    {
      value: 'inside',
      label: 'Always Show',
      helpText: 'Dropdown is always visible above or next to the main CTA button',
    },
    {
      value: 'outside',
      label: 'Popup',
      helpText: 'Dropdown appears when clicking the main CTA button',
    },
  ];

  badgeDisplayTypeOptions = [
    { value: 'badge', label: 'As Badge', helpText: 'Overlay badge on product image' },
    { value: 'text', label: 'As Text', helpText: 'Show savings below price' },
  ];

  imageFitOptions = [
    { value: 'square', label: 'Fit To Square', helpText: 'Crops to a square thumbnail' },
    { value: 'original', label: 'Original Size', helpText: 'Shows the full image as uploaded on Shopify' },
  ];

  discountTypeOptions = [
    { label: 'Fixed amount', value: CheckoutUpsellExtension.DiscountType.FixedAmount },
    { label: 'Percentage', value: CheckoutUpsellExtension.DiscountType.Percentage },
  ];

  labelColorOptions = [
    { label: 'Default', value: 'default' },
    { label: 'Base', value: 'base' },
    { label: 'Info', value: 'info' },
    { label: 'Critical', value: 'critical' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Accent', value: 'accent' },
  ];

  discountApplyToModeOptions: { label: string; value: GroupDiscountApplyToMode }[] = [
    { label: 'All Products', value: 'all' },
    { label: 'Manual', value: 'manual' },
    { label: 'Smart', value: 'smart' },
  ];

  colorOptions = Object.values(CheckoutExtensionUI.Appearance).map((value) => ({
    label: capitalize(value),
    value,
  }));

  cornerRadiusOptions = Object.values(CheckoutExtensionUI.CornerRadius).map((value) => ({
    label: capitalize(value),
    value,
  }));

  mainBackgroundCornerRadiusOptions = Object.values(CheckoutExtensionUI.CornerRadius)
    .filter((value) => value !== CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED)
    .map((value) => ({
      label: capitalize(value),
      value,
    }));

  borderOptions = Object.values(CheckoutExtensionUI.BorderType).map((value) => ({
    label: capitalize(value),
    value,
  }));

  borderWidthOptions = Object.values(CheckoutExtensionUI.BorderWidth).map((value) => ({
    label: capitalize(value),
    value,
  }));

  paddingOptions = Object.values(CheckoutExtensionUI.Padding).map((value) => ({
    label: capitalize(value),
    value,
  }));

  Appearances = ['default', ...Object.values(CheckoutExtensionUI.Appearance)];
  appearanceToDisplayText = (appearance: CheckoutExtensionUI.Appearance) => capitalize(appearance);

  constructor(
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
    private dialog: MatDialog,
  ) {}

  get ruleGroupsArray(): UntypedFormArray {
    return this.formGroup?.get(this.ruleGroupsFieldName) as UntypedFormArray;
  }

  get additionalDisplayTextsFormArray(): UntypedFormArray {
    return this.formGroup.get('settings.additionalDisplayTexts') as UntypedFormArray;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get productReviewsFormGroup(): UntypedFormGroup {
    return this.formGroup.get('settings.productReviews') as UntypedFormGroup;
  }

  addRuleGroup(): void {
    const newId = this.getNextRuleGroupId();
    const ruleGroupFormGroup = createRuleGroupFormGroup(
      newId,
      { ...DEFAULT_RULE_GROUP, id: newId },
      this.rulesDynamicFormService,
    );
    this.ruleGroupsArray.push(ruleGroupFormGroup);
  }

  removeRuleGroup(index: number): void {
    if (this.ruleGroupsArray.length > 1) {
      this.ruleGroupsArray.removeAt(index);
    }
  }

  duplicateRuleGroup(index: number): void {
    const sourceGroup = this.ruleGroupsArray.at(index) as UntypedFormGroup;
    const newId = this.getNextRuleGroupId();
    const duplicatedValues = { ...sourceGroup.value, id: newId };
    const duplicatedGroup = createRuleGroupFormGroup(newId, duplicatedValues, this.rulesDynamicFormService);
    this.ruleGroupsArray.push(duplicatedGroup);
  }

  private getNextRuleGroupId(): number {
    const existingIds = this.ruleGroupsArray.value.map((group: { id: number }) => group.id);
    return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;
  }

  getRuleGroupFormGroup(index: number): UntypedFormGroup {
    return this.ruleGroupsArray.at(index) as UntypedFormGroup;
  }

  getProductsInCartMode(index: number): ProductsInCartMode {
    return (
      this.getRuleGroupFormGroup(index)?.get('productsInCart.mode')?.value ??
      CheckoutUpsellExtension.ProductsInCartMode.ANY
    );
  }

  setProductsInCartMode(index: number, mode: ProductsInCartMode): void {
    this.getRuleGroupFormGroup(index)?.get('productsInCart.mode')?.setValue(mode);
  }

  getUpsellProductsMode(index: number): UpsellRecommendedProductsMode {
    return (
      this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.mode')?.value ??
      CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL
    );
  }

  setUpsellProductsMode(index: number, mode: UpsellRecommendedProductsMode): void {
    this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.mode')?.setValue(mode);

    if (mode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS) {
      this.getRuleGroupFormGroup(index)?.get('applyDiscountToRecommendedProducts')?.setValue(false);
    }

    const priorityControl = this.getRuleGroupFormGroup(index)?.get('prioritizeRecommendedItemsBy');

    if (
      mode !== CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL &&
      priorityControl?.value === CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.MANUAL
    ) {
      return priorityControl.setValue(CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.LOW_PRICE);
    }

    return;
  }

  getPrioritizeOptionsForMode(index: number): PriorityOption[] {
    const mode = this.getUpsellProductsMode(index);
    if (mode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL) {
      return this.prioritizeOptions;
    }
    return this.prioritizeOptions.filter(
      (option) => option.value !== CheckoutUpsellExtension.PrioritizeRecommendedItemsBy.MANUAL,
    );
  }

  getUpsellProducts(index: number): UpsellProduct[] {
    return this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.value ?? [];
  }

  getUpsellProductsForDisplay(index: number): any[] {
    const products = this.getUpsellProducts(index);
    return products.map((p) => ({
      ...p,
      name: p.displayName || p.name,
      description: p.displayDescription || p.description,
    }));
  }

  getRuleGroupSummary(index: number): string {
    const cartMode = this.getProductsInCartMode(index);
    const upsellMode = this.getUpsellProductsMode(index);

    const cartCondition = cartMode === 'any' ? 'Any Products' : 'Smart Selection';

    let upsellType = '';
    switch (upsellMode) {
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS:
        upsellType = 'Automatic';
        break;
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL:
        const products = this.getUpsellProducts(index);
        upsellType = `Manual (${products.length} products)`;
        break;
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.SMART:
        upsellType = 'Smart';
        break;
    }

    return `${cartCondition} → ${upsellType}`;
  }

  getUpsellProductsHelperText(mode: UpsellRecommendedProductsMode): string {
    switch (mode) {
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS:
        return 'Uses Shopify recommendations';
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL:
        return 'Add products manually one by one';
      case CheckoutUpsellExtension.UpsellRecommendedProductsMode.SMART:
        return 'Existing and future products that match the conditions you set';
      default:
        return '';
    }
  }

  getDiscountsFormArray(groupIndex: number): UntypedFormArray {
    return this.getRuleGroupFormGroup(groupIndex)?.get('discounts') as UntypedFormArray;
  }

  getDiscountFormGroup(groupIndex: number, discountIndex: number): UntypedFormGroup {
    return this.getDiscountsFormArray(groupIndex)?.at(discountIndex) as UntypedFormGroup;
  }

  addDiscountGroup(groupIndex: number): void {
    const nextId = this.getNextDiscountGroupId(groupIndex);
    this.getDiscountsFormArray(groupIndex).push(createGroupDiscountFormGroup(nextId, {}, this.rulesDynamicFormService));
    this.formGroup.markAsDirty();
  }

  duplicateDiscountGroup(groupIndex: number, discountIndex: number): void {
    const sourceDiscountValues = this.getDiscountFormGroup(groupIndex, discountIndex).value;
    const nextId = this.getNextDiscountGroupId(groupIndex);
    this.getDiscountsFormArray(groupIndex).push(
      createGroupDiscountFormGroup(nextId, { ...sourceDiscountValues, id: nextId }, this.rulesDynamicFormService),
    );
    this.formGroup.markAsDirty();
  }

  private getNextDiscountGroupId(groupIndex: number): number {
    const existingIds: number[] = this.getDiscountsFormArray(groupIndex).value.map(
      (discount: { id: number }) => discount.id,
    );
    return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 0;
  }

  removeDiscountGroup(groupIndex: number, discountIndex: number): void {
    this.getDiscountsFormArray(groupIndex).removeAt(discountIndex);
    if (this.getDiscountsFormArray(groupIndex)?.length === 0) {
      this.getRuleGroupFormGroup(groupIndex)?.get('applyDiscountToRecommendedProducts')?.setValue(false);
    }
    this.formGroup.markAsDirty();
  }

  onApplyDiscountToggled(groupIndex: number, enabled: boolean): void {
    if (enabled && this.getDiscountsFormArray(groupIndex)?.length === 0) {
      this.addDiscountGroup(groupIndex);
    }
  }

  getDiscountApplyToMode(groupIndex: number, discountIndex: number): GroupDiscountApplyToMode {
    return this.getDiscountFormGroup(groupIndex, discountIndex)?.get('applyTo.mode')?.value ?? 'all';
  }

  getDiscountProducts(groupIndex: number, discountIndex: number): UpsellProduct[] {
    return this.getDiscountFormGroup(groupIndex, discountIndex)?.get('applyTo.products')?.value ?? [];
  }

  getDiscountProductsSummary(groupIndex: number, discountIndex: number): string {
    const products = this.getDiscountProducts(groupIndex, discountIndex);
    const names = products.slice(0, 3).map((product) => product.displayName || product.name);
    const remainingCount = products.length - names.length;
    return remainingCount > 0 ? `${names.join(', ')}, +${remainingCount}` : names.join(', ');
  }

  openDiscountProductsModal(groupIndex: number, discountIndex: number): void {
    const selectedProducts = this.getDiscountProducts(groupIndex, discountIndex);

    const config: AddProductsModalConfig = {
      title: 'Select Products',
      searchPlaceholder: 'Search product by name or SKU',
      addButtonText: 'Save',
      preSelectedProducts: selectedProducts.map((product) => ({
        ...product,
        title: product.displayName || product.name,
      })),
      lockedProductIds: this.getProductIdsClaimedByOtherDiscounts(groupIndex, discountIndex),
      isMultiSelect: true,
      fetchProducts: (query) => this.fetchDiscountPickerProducts(groupIndex, query),
    };

    const dialogRef = this.dialog.open(AddProductsModalComponent, {
      data: config,
      panelClass: 'add-products-modal-container',
      width: '700px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((result: SelectableProduct[] | undefined) => {
      if (!result) {
        return;
      }
      const pickedProducts: UpsellProduct[] = result.map((product) => ({
        id: product.id,
        name: product.title,
        description: product.description,
        imageUrl: product.imageUrl,
        sku: product.sku,
        price: product.price,
      }));
      this.getDiscountFormGroup(groupIndex, discountIndex)?.get('applyTo.products')?.setValue(pickedProducts);
      this.formGroup.markAsDirty();
    });
  }

  // Only the first matching discount applies at runtime; own products stay editable so saved
  // overlaps are not dropped.
  private getProductIdsClaimedByOtherDiscounts(groupIndex: number, discountIndex: number): (string | number)[] {
    const ownProductIds = new Set(this.getDiscountProducts(groupIndex, discountIndex).map((product) => product.id));
    const claimedProductIds = new Set<string | number>();

    (this.getDiscountsFormArray(groupIndex)?.controls ?? []).forEach((discountControl, index) => {
      if (index === discountIndex || discountControl.get('applyTo.mode')?.value !== 'manual') {
        return;
      }
      const products: UpsellProduct[] = discountControl.get('applyTo.products')?.value ?? [];
      products.forEach((product) => {
        if (!ownProductIds.has(product.id)) {
          claimedProductIds.add(product.id);
        }
      });
    });

    return [...claimedProductIds];
  }

  // The discount only ever targets the group's configured product list — for a manual group that is
  // the picked products themselves; for smart/automatic groups it falls back to the store search.
  private async fetchDiscountPickerProducts(groupIndex: number, searchQuery: string): Promise<SelectableProduct[]> {
    const groupMode = this.getUpsellProductsMode(groupIndex);
    if (groupMode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL) {
      const normalizedQuery = (searchQuery ?? '').toLowerCase();
      return this.getUpsellProducts(groupIndex)
        .map((product) => ({
          ...product,
          title: product.displayName || product.name,
        }))
        .filter(
          (product) =>
            !normalizedQuery ||
            String(product.title ?? '')
              .toLowerCase()
              .includes(normalizedQuery) ||
            String(product['sku'] ?? '')
              .toLowerCase()
              .includes(normalizedQuery),
        );
    }
    if (groupMode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.SMART) {
      return this.fetchProductsFromShopify(groupIndex, groupMode, searchQuery);
    }
    return firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: searchQuery }));
  }

  onAddProducts(index: number): void {
    const currentProducts: UpsellProductWithDetails[] = this.getUpsellProducts(index);
    const mode = this.getUpsellProductsMode(index);

    const config: AddProductsModalConfig = {
      title: currentProducts.length ? 'Edit Upsell Products' : 'Add Upsell Products',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      enableEdit: true,
      allowEmptySelection: true,
      enableProductEdit: true,
      openProductInShopify: (product) => this.onExternalLink(product),
      preSelectedProducts: currentProducts.map((product) => ({
        id: product.id,
        title: product.name ?? String(product.id),
        sku: product.sku,
        price: product.price,
        imageUrl: product.imageUrl,
        displayName: product.displayName,
        displayDescription: product.displayDescription,
        selectedOptions: product.selectedOptions,
      })),
      enableVariants: true,
      fetchProducts: (query) => this.fetchProductsFromShopify(index, mode, query),
    };

    const dialogRef = this.dialog.open(AddProductsModalComponent, {
      data: config,
      panelClass: 'add-products-modal-container',
      width: '700px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((result: SelectableProduct[] | undefined) => {
      if (!result) return;
      const updatedProducts = mergeEditedProducts(
        currentProducts,
        result,
        (p): UpsellProductWithDetails => ({
          id: p.id,
          name: p.title,
          description: p.description,
          imageUrl: p.imageUrl,
          sku: p.sku,
          price: p.price,
          displayName: p.displayName,
          displayDescription: p.displayDescription,
          selectedOptions: p.selectedOptions,
        }),
        (kept, selected) => ({
          ...kept,
          displayName: selected.displayName,
          displayDescription: selected.displayDescription,
          selectedOptions: selected.selectedOptions,
        }),
      );
      this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.setValue(updatedProducts);
    });
  }

  private async fetchProductsFromShopify(
    index: number,
    mode: UpsellRecommendedProductsMode,
    searchQuery: string,
  ): Promise<SelectableProduct[]> {
    if (mode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL) {
      return firstValueFrom(
        this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: searchQuery }),
      );
    }

    if (mode === CheckoutUpsellExtension.UpsellRecommendedProductsMode.SMART) {
      const rules = this.getUpsellProductsRules(index);
      const rulesQuery = mapExtensionRulesToGqlQuery(rules);
      return firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: rulesQuery }));
    }

    return [];
  }

  private getUpsellProductsRules(index: number): DynamicRule[] {
    const rulesFormArray = this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.rules');
    if (!rulesFormArray) {
      return [];
    }
    return rulesFormArray.value as DynamicRule[];
  }

  onRemoveProduct(index: number, productId: string | number): void {
    const products = this.getUpsellProducts(index)
      .filter((p) => p.id !== productId)
      .map((p, priority) => ({ ...p, priority }));
    this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.setValue(products);
  }

  onReorderProducts(index: number, reorderedProducts: ProductListItem[]): void {
    const currentProducts = this.getUpsellProducts(index);
    const updatedProducts = reorderedProducts.map((reordered, priority) => ({
      ...currentProducts.find((p) => p.id === reordered.id),
      priority,
    }));
    this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.setValue(updatedProducts);
  }

  onEditProduct(index: number, product: ProductListItem): void {
    const savedProduct = this.getUpsellProducts(index).find((p) => p.id === product.id);
    this.dialog
      .open(EditProductModalComponent, {
        data: {
          product: {
            ...product,
            name: savedProduct?.name,
            description: savedProduct?.description,
            displayName: savedProduct?.displayName ?? '',
            displayDescription: savedProduct?.displayDescription ?? '',
          },
        },
        width: '650px',
      })
      .afterClosed()
      .subscribe((result: EditProductModalResult | undefined) => {
        if (!result) return;
        const updatedProducts = this.getUpsellProducts(index).map((p) =>
          p.id === product.id
            ? { ...p, displayName: result.displayName, displayDescription: result.displayDescription }
            : p,
        );
        this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.setValue(updatedProducts);
      });
  }

  onExternalLink(product: any): void {
    const productId =
      typeof product.id === 'string' && product.id.includes('/') ? product.id.split('/').pop() : product.id;

    const shop = this.shopService.shopSignal();
    const trimmedShopName = shop.name.split('.myshopify.com')[0];
    window.open(`https://admin.shopify.com/store/${trimmedShopName}/products/${productId}`, '_blank');
  }

  getHideProductsValue(index: number): HideProductsAlreadyInCart {
    return this.getRuleGroupFormGroup(index)?.get('hideProductsAlreadyInCart')?.value ?? 'none';
  }

  setHideProductsValue(index: number, value: HideProductsAlreadyInCart): void {
    this.getRuleGroupFormGroup(index)?.get('hideProductsAlreadyInCart')?.setValue(value);
  }

  toggleHideProducts(index: number, enabled: boolean): void {
    if (enabled) {
      this.setHideProductsValue(index, 'parentProduct');
    } else {
      this.setHideProductsValue(index, 'none');
      this.setHideProductsValue(index, 'none');
    }
  }

  onViewProducts(index: number): void {
    this.loadingRuleGroupIndex = index;
    this.loadingRuleGroupIndex = index;
    const rules = this.getUpsellProductsRules(index);
    const query = mapExtensionRulesToGqlQuery(rules);
    this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query }).subscribe({
      next: (products) => {
        const upsellProducts: UpsellProduct[] = products.map((p, idx) => ({
          id: p.id,
          name: p.title,
          description: p.description,
          priority: idx,
          imageUrl: p.imageUrl,
          sku: p.sku,
          price: p.price,
        }));
        this.getRuleGroupFormGroup(index)?.get('upsellRecommendedProducts.products')?.setValue(upsellProducts);
        this.loadingRuleGroupIndex = null;
        this.loadingRuleGroupIndex = null;
      },
      error: (err) => {
        console.error('Failed to fetch products:', err);
        this.loadingRuleGroupIndex = null;
        this.loadingRuleGroupIndex = null;
      },
    });
  }

  isLoadingProducts(index: number): boolean {
    return this.loadingRuleGroupIndex === index;
  }

  drop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.additionalDisplayTextsFormArray.controls, event.previousIndex, event.currentIndex);
    this.additionalDisplayTextsFormArray.controls.forEach((control, index) => {
      control.get('priority').setValue(index);
    });
    this.formGroup.markAsDirty();
  }

  onAddExcludedReviewProducts(): void {
    const currentProducts: UpsellProduct[] = this.productReviewsFormGroup.get('excludeProducts').value ?? [];

    const config: AddProductsModalConfig = {
      title: 'Exclude Products From Reviews',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      excludeProductIds: currentProducts.map((product) => product.id),
      fetchProducts: (query) =>
        firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query })),
    };

    this.dialog
      .open(AddProductsModalComponent, {
        data: config,
        panelClass: 'add-products-modal-container',
        width: '700px',
        maxHeight: '80vh',
      })
      .afterClosed()
      .subscribe((selected: SelectableProduct[] | undefined) => {
        if (!selected?.length) return;

        const added: UpsellProduct[] = selected.map((product) => ({
          id: product.id,
          name: product.title,
          description: product.description,
          imageUrl: product.imageUrl,
          sku: product.sku,
          price: product.price,
        }));

        this.productReviewsFormGroup.get('excludeProducts').setValue([...currentProducts, ...added]);
        this.formGroup.markAsDirty();
      });
  }

  onRemoveExcludedReviewProduct(productId: string | number): void {
    const currentProducts: UpsellProduct[] = this.productReviewsFormGroup.get('excludeProducts').value ?? [];
    const remaining = currentProducts.filter((product) => String(product.id) !== String(productId));

    this.productReviewsFormGroup.get('excludeProducts').setValue(remaining);
    this.formGroup.markAsDirty();
  }

  setDisplayType(value: string): void {
    this.formGroup.get('style.displayType')?.setValue(value);
    this.formGroup.markAsDirty();
  }

  get isProductDisplayEnabled(): boolean {
    return this.formGroup.get('style.displayType')?.value !== CheckoutUpsellExtension.DisplayType.HIDE_PRODUCTS;
  }
}
